import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Formik as FormikBase, Form as FormBase } from "formik";
import type { FormikHelpers, FormikValues } from "formik";
import {
  Row as RowBase,
  Col as ColBase,
  Card as CardBase,
} from "react-bootstrap";
import { Button, Icon as IconBase, Typography } from "@mui/material";

import { getFormConfig } from "../utils/formConfigs";
import { logger } from "@/helpers/loggerHelper";
import FormField from "./FormField";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { requestService } from "../services/requestService";
import RpMUIModalBase from "@/components/modals/RpMUIModal";
import { BRAND_REQUEST_TYPE, HTTP_STATUS } from "@/config/constants";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import { formatDateForApiRequest } from "@/helpers/utils";
import { shouldBlockUnsafeSubmission } from "@/helpers/sanitizeHelper";

type SelectedBrand = {
  id?: number | string;
  status?: boolean | number;
};

type DateRangeValue = {
  endDate?: unknown;
  startDate?: unknown;
};

type DynamicFormValues = FormikValues & {
  budgetDateRange?: DateRangeValue | null;
  budget_range?: { max?: unknown; min?: unknown } | string | null;
  campaign_type?: string;
  date_range_end_date?: unknown;
  date_range_start_date?: unknown;
  fixed_budget?: unknown;
  flight_dates?: DateRangeValue | DateRangeValue[] | null;
  fk_group_id?: Array<number | string>;
  fk_location_id?: Array<number | string> | null;
  location_details?: unknown;
  new_location?: boolean | string;
  reportRequestDateRange?: DateRangeValue | null;
};

type FieldConfig = {
  conditional?: (_values: DynamicFormValues) => boolean;
  name: string;
  [key: string]: unknown;
};

type FormConfig = {
  fields: FieldConfig[];
  initialValues: DynamicFormValues;
  validationSchema?: unknown;
};

type DynamicFormProps = {
  brandRequestList?: unknown;
  campaignGoals?: unknown[];
  campaigns?: unknown[];
  channels?: unknown[];
  groups?: unknown[];
  locations?: Array<Record<string, unknown>>;
  onSuccess?: () => void;
  requestType: string;
  setActiveTab?: (_tabIndex: number) => void;
};

const Formik = FormikBase as unknown as React.ComponentType<{
  children: (_props: {
    isSubmitting: boolean;
    setFieldValue: (_field: string, _value: unknown) => void;
    values: DynamicFormValues;
  }) => React.ReactNode;
  enableReinitialize?: boolean;
  initialValues: DynamicFormValues;
  key?: string;
  onSubmit: (
    _values: DynamicFormValues,
    _actions: FormikHelpers<DynamicFormValues>
  ) => void | Promise<void>;
  validateOnChange?: boolean;
  validateOnMount?: boolean;
  validationSchema?: unknown;
}>;

const Form = FormBase as unknown as React.ComponentType<{
  children: React.ReactNode;
  noValidate?: boolean;
}>;

const Row = RowBase as unknown as React.ComponentType<{
  children: React.ReactNode;
}>;

const Col = ColBase as unknown as React.ComponentType<{
  children: React.ReactNode;
  className?: string;
  key?: string;
  lg?: number;
  md?: number;
  xs?: number;
}>;

const Card = CardBase as unknown as React.ComponentType<{
  children: React.ReactNode;
  className?: string;
}>;

const CardBody = CardBase.Body as unknown as React.ComponentType<{
  children: React.ReactNode;
  className?: string;
}>;

const CardFooter = CardBase.Footer as unknown as React.ComponentType<{
  children: React.ReactNode;
  className?: string;
}>;

const Icon = IconBase as unknown as React.ComponentType<{
  color?: string;
  component: React.ComponentType<Record<string, unknown>>;
  name?: string;
  size?: string;
  style?: React.CSSProperties;
}>;

const RpMUIModal = RpMUIModalBase as unknown as React.ComponentType<{
  children: React.ReactNode;
  onClose: () => void;
  open: boolean;
  persists?: boolean;
  size?: string;
}>;

const DynamicForm = ({
  requestType,
  channels,
  brandRequestList,
  locations: initialLocations,
  groups,
  setActiveTab = () => {},
  onSuccess,
  campaigns,
  campaignGoals,
}: DynamicFormProps) => {
  const loader = useContext(LoaderContext);
  const { selectedBrand } = useContext(GlobalContext) as {
    selectedBrand?: SelectedBrand;
  };
  const [loading, setLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [locations, setLocations] = useState(initialLocations || []);
  const prevGroupIdsRef = useRef<Array<number | string>>([]);

  const fetchLocationsByGroups = useCallback(
    async (groupIds: Array<number | string>) => {
      if (!groupIds || groupIds.length === 0) {
        setLocations([]);
        return;
      }

      try {
        setLoading(true);
        const response = await requestService.getBrandLocations({
          groupId: groupIds.join(","),
          id: selectedBrand?.id,
        });

        if (response.status === HTTP_STATUS.OK) {
          setLocations(response.data || []);
        } else {
          logger.error("Failed to fetch locations for groups:", groupIds);
          setLocations([]);
        }
      } catch (error) {
        logger.error("Error fetching locations for groups:", error);
        setLocations([]);
      } finally {
        setLoading(false);
      }
    },
    [selectedBrand?.id]
  );

  // Function to fetch all locations (for non-NEW_CAMPAIGN request types)
  const fetchAllLocations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await requestService.getBrandLocations({
        id: selectedBrand?.id,
      });

      if (response.status === HTTP_STATUS.OK) {
        setLocations(response.data || []);
      } else {
        logger.error("Failed to fetch all locations");
        setLocations([]);
      }
    } catch (error) {
      logger.error("Error fetching all locations:", error);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.id]);

  // Get form configuration based on request type
  const formConfig = getFormConfig({
    requestType,
    channels,
    brandRequestList,
    locations,
    groups,
    campaigns: campaigns || [],
    campaignGoals: campaignGoals || [],
  }) as FormConfig;

  // Handle location state updates based on request type changes
  useEffect(() => {
    if (requestType === BRAND_REQUEST_TYPE.NEW_CAMPAIGN) {
      // For NEW_CAMPAIGN, clear locations and reset group tracking
      setLocations([]);
      prevGroupIdsRef.current = [];
    } else {
      // For other request types, fetch all locations
      fetchAllLocations();
    }
  }, [requestType, fetchAllLocations]);

  useEffect(() => {
    loader(loading);
  }, [loading, loader]);

  /**
   * Handle form submission
   * @param {Object} values - Form values
   * @param {Object} actions - Formik actions
   */
  const handleSubmit = async (
    values: DynamicFormValues,
    actions: FormikHelpers<DynamicFormValues>
  ) => {
    setLoading(true);

    if (shouldBlockUnsafeSubmission(values)) {
      setLoading(false);
      actions.setSubmitting(false);
      return;
    }

    try {
      const fd = new FormData();
      fd.append("fk_brand_id", selectedBrand?.id as unknown as string);
      fd.append("name", requestType);

      if (values?.budgetDateRange) {
        values.date_range_start_date = formatDateForApiRequest(
          values?.budgetDateRange?.startDate
        );
        values.date_range_end_date = formatDateForApiRequest(
          values?.budgetDateRange?.endDate
        );
        values.budgetDateRange = null;
      } else if (values?.reportRequestDateRange) {
        values.date_range_start_date = formatDateForApiRequest(
          values?.reportRequestDateRange?.startDate
        );
        values.date_range_end_date = formatDateForApiRequest(
          values?.reportRequestDateRange?.endDate
        );
      }
      if (values?.flight_dates) {
        // Only process if campaign type is relevant
        if (
          values.campaign_type === "Multiple Windows" &&
          Array.isArray(values.flight_dates)
        ) {
          values.flight_dates
            .filter((range) => range?.startDate && range?.endDate) // skip incomplete
            .forEach((range, index) => {
              fd.append(
                `flight_dates[${index}][date_range_start_date]`,
                range.startDate as string
              );
              fd.append(
                `flight_dates[${index}][date_range_end_date]`,
                range.endDate as string
              );
            });
        } else if (
          values.campaign_type !== "Multiple Windows" &&
          !Array.isArray(values.flight_dates)
        ) {
          const range = values.flight_dates;
          if (range?.startDate && range?.endDate) {
            // skip if incomplete
            fd.append(
              `flight_dates[0][date_range_start_date]`,
              range.startDate as string
            );
            fd.append(
              `flight_dates[0][date_range_end_date]`,
              range.endDate as string
            );
          }
        }

        // Clear after processing to avoid duplication
        values.flight_dates = null;
      }

      if (values.budget_range && values.campaign_type === "limitedTime") {
        values.fixed_budget = null;
      } else if (values.fixed_budget && values.campaign_type === "OTS") {
        values.budget_range = null;
      }
      if (values.new_location) {
        values.fk_location_id = null;
      } else if (!values.new_location) {
        values.location_details = null;
      }
      // Clear flight_dates for Fixed Dates campaign type
      const fixedDateRange = Array.isArray(values.flight_dates)
        ? undefined
        : values.flight_dates;
      if (
        values.campaign_type === "Fixed Dates" &&
        fixedDateRange &&
        fixedDateRange.startDate &&
        fixedDateRange.endDate
      ) {
        values.flight_dates = null;
      }
      if (values.new_location) {
        values.new_location = values.new_location === true ? "Yes" : "No";
      }
      // Format budget_range as "min-max"
      if (values.budget_range && typeof values.budget_range === "object") {
        const { min, max } = values.budget_range;
        values.budget_range = `${min}-${max}`;
      }

      Object.keys(values).forEach((k) => {
        const value = values[k];
        if (k === "flight_dates") {
          // Skip flight_dates as it's handled separately above
          return;
        }
        if (Array.isArray(value) && value[0] instanceof File) {
          // Append each file separately with the same field name
          value.forEach((file) => {
            fd.append(k, file);
          });
        } else if (k === "fk_group_id" && Array.isArray(value)) {
          // Only include group IDs for NEW_CAMPAIGN request type
          if (
            requestType === BRAND_REQUEST_TYPE.NEW_CAMPAIGN &&
            value.length > 0
          ) {
            fd.append(k, value.join(","));
          }
        } else if (k === "fk_location_id" && Array.isArray(value)) {
          // Handle location IDs for all request types
          if (value.length > 0) {
            fd.append(k, value.join(","));
          }
        } else {
          values[k] && fd.append(k, values[k] as string | Blob);
        }
      });

      const res = await requestService.brandRequestCreate(fd);
      if (res.status === HTTP_STATUS.OK) {
        setShowSuccessDialog(true);
        actions.resetForm({
          values: formConfig.initialValues,
        });
        setTimeout(() => {
          setActiveTab(1);
          if (onSuccess && typeof onSuccess === "function") {
            onSuccess();
          }
        }, 4000);
      }
    } catch (err) {
      setLoading(false);
      logger.trace(err);
    } finally {
      setLoading(false);
    }
  };
  const renderForm = () => {
    return (
      <Formik
        key={requestType} // Key prop ensures form reset on request type change
        initialValues={formConfig.initialValues}
        validationSchema={formConfig.validationSchema}
        onSubmit={handleSubmit}
        validateOnMount={false} // Prevent validation on initial render
        enableReinitialize={true} // Enable form reinitialization on prop changes
        validateOnChange
      >
        {({ isSubmitting, values, setFieldValue }) => {
          if (requestType === BRAND_REQUEST_TYPE.NEW_CAMPAIGN) {
            const currentGroupIds = Array.isArray(values.fk_group_id)
              ? values.fk_group_id
              : [];
            const groupsChanged =
              JSON.stringify(currentGroupIds) !==
              JSON.stringify(prevGroupIdsRef.current);
            if (groupsChanged) {
              prevGroupIdsRef.current = currentGroupIds;
              if (currentGroupIds.length > 0) {
                fetchLocationsByGroups(currentGroupIds);
              } else {
                setLocations([]);
              }

              // Clear location selection when groups change
              if (values.fk_location_id) {
                setFieldValue("fk_location_id", "");
              }
            }
          }

          return (
            <Form noValidate>
              <Card className="border-0">
                <CardBody className="p-0">
                  <Row>
                    {formConfig.fields.map(
                      (field) =>
                        (!field.conditional || field.conditional(values)) && (
                          <Col
                            key={field.name}
                            xs={12}
                            className="mt-2"
                            md={12}
                            lg={12}
                          >
                            <FormField
                              {...field}
                              channels={channels}
                              requestType={requestType}
                            />
                          </Col>
                        )
                    )}
                  </Row>
                </CardBody>
                {selectedBrand && selectedBrand?.status ? (
                  <CardFooter className="bg-transparent px-0 pt-5 border-0">
                    <div className="d-flex justify-content-end">
                      <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={isSubmitting}
                        size="large"
                      >
                        Submit Request
                      </Button>
                    </div>
                  </CardFooter>
                ) : null}
              </Card>
            </Form>
          );
        }}
      </Formik>
    );
  };

  return (
    <div>
      {renderForm()}
      <RpMUIModal
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        size="xl"
        persists
      >
        <div className="text-center p-4">
          <Icon
            component={RpSvgIcon}
            name="success_icon"
            color="primary"
            size="large"
            style={{ fontSize: "8.5rem" }}
          />
          {/* <CheckCircleIcon style={{ fontSize: 100 }} color="primary" /> */}
          <Typography
            variant="h3"
            className="fw-b my-4"
            color="text.primary"
            gutterBottom
          >
            Success!
          </Typography>
          <Typography className="fs-16 fw-sb" color="initial">
            Your request has been successfully submitted and recorded. Check
            your email for a record of your request and next steps.
          </Typography>
        </div>
      </RpMUIModal>
    </div>
  );
};

export default DynamicForm;
